
import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { painelDialogSurfaceClass } from '@/components/painel/painel-styles';
import { buildInlineSmilieImgHtml, normalizeSmilieNameInput, SMILIE_INNER_RE } from '@/lib/cms-rich-helpers';
import { XAT_API } from '@/services/api-paths';

export type InsertSmilieDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onInsert: (html: string) => void;
  dark?: boolean;
  contentClassName?: string;
};

export function InsertSmilieDialog({
  open,
  onOpenChange,
  onInsert,
  dark = true,
  contentClassName,
}: InsertSmilieDialogProps) {
  const [raw, setRaw] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [resolvedName, setResolvedName] = useState('');

  const reset = useCallback(() => {
    setRaw('');
    setChecking(false);
    setError('');
    setPreviewUrl(null);
    setResolvedName('');
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const validateAndPreview = async () => {
    setError('');
    setPreviewUrl(null);
    const name = normalizeSmilieNameInput(raw);
    if (!name || !SMILIE_INNER_RE.test(name)) {
      setError('Use apenas letras, números, _ ou - (ex.: smile, biggrin).');
      return;
    }
    setChecking(true);
    try {
      const res = await fetch(XAT_API.smilieValidate(name), {
        cache: 'no-store',
      });
      const data = (await res.json()) as {
        results?: Record<string, { ok: boolean; url?: string }>;
      };
      const r = data.results?.[name];
      if (!r?.ok || !r.url) {
        setError('Este smilie não foi encontrado no xat.');
        setResolvedName('');
        return;
      }
      setResolvedName(name);
      setPreviewUrl(r.url);
    } catch {
      setError('Não foi possível validar. Tente de novo.');
    } finally {
      setChecking(false);
    }
  };

  const insert = () => {
    if (!resolvedName || !previewUrl) {
      void validateAndPreview();
      return;
    }
    onInsert(buildInlineSmilieImgHtml(resolvedName, previewUrl));
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-sm gap-4',
          dark && painelDialogSurfaceClass,
          contentClassName,
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(dark && 'font-poppins text-white')}>Inserir smilie</DialogTitle>
          <DialogDescription className={cn(dark && 'text-blue-200/80')}>
            Digite o nome do smilie (como no chat). Só é inserido se existir no xat. Também pode escrever{' '}
            <code className="rounded bg-white/10 px-1">(nome)</code> no texto — será convertido automaticamente quando
            for um código válido.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          <Label className={cn(dark && 'text-blue-100')}>Nome do smilie</Label>
          <Input
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="ex.: smile ou (smile)"
            className={cn(dark && 'border-white/15 bg-white/5 text-white placeholder:text-slate-500')}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), void validateAndPreview())}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={checking || !raw.trim()}
            className={cn(dark && 'border-white/15 bg-white/10 text-white hover:bg-white/15')}
            onClick={() => void validateAndPreview()}
          >
            {checking ? 'A validar…' : 'Pré-visualizar'}
          </Button>
        </div>

        {error ? <p className="text-sm text-red-400">{error}</p> : null}

        {previewUrl ? (
          <div
            className={cn(
              'flex items-center gap-3 rounded-xl border p-3',
              dark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50',
            )}
          >
            <img src={previewUrl} alt={resolvedName} className="h-10 w-10 object-contain" />
            <span className={cn('text-sm', dark ? 'text-sky-100' : 'text-gray-800')}>
              ({resolvedName})
            </span>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            className={cn(dark && 'border-white/20 bg-transparent text-blue-100 hover:bg-white/10')}
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className={cn(dark && 'bg-sky-600 hover:bg-sky-500')}
            disabled={!previewUrl || checking}
            onClick={insert}
          >
            Inserir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
